// Split out of `SpatialResultsMapPanel.tsx` to keep it ≤200 lines. Bundles two small derivations
// that share the same decoded-feature input: the distinct-SRIDs forward (T15's filter chip) and
// T28's `probeContext` (the coordinate probe's gate + engine/srid source) — both are cheap `useMemo`s
// over data the panel already decodes, no new work.

import { useEffect, useMemo } from 'react';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { SpatialMapFeature, SpatialProbeContext } from '../map/spatialMapTypes';
import { deriveProbeContext } from './spatialResultsMapView';

export function useSpatialProbeContext(args: {
  decodedFeatures: SpatialMapFeature[];
  engine: DatabaseEngineId;
  geomId: string | null;
  isDeclared: boolean;
  dataType: string | undefined;
  surface: SpatialProbeContext['surface'];
  onSridsChange?: (srids: number[]) => void;
}): { distinctSrids: number[]; probeContext: SpatialProbeContext | null } {
  // Forward the distinct SRIDs of the decoded window to the caller (T15 filter
  // chip). >1 distinct SRID → the chip stamps `srid = null` (never a guess).
  const distinctSrids = useMemo(
    () =>
      Array.from(
        new Set(
          args.decodedFeatures
            .map((f) => f.geometry.srid)
            .filter((s): s is number => typeof s === 'number'),
        ),
      ).sort((a, b) => a - b),
    [args.decodedFeatures],
  );
  const onSridsChange = args.onSridsChange;
  useEffect(() => {
    onSridsChange?.(distinctSrids);
  }, [distinctSrids, onSridsChange]);

  const probeContext = useMemo(
    () =>
      deriveProbeContext({
        engine: args.engine,
        hasGeometryColumn: Boolean(args.geomId),
        isDeclared: args.isDeclared,
        dataType: args.dataType,
        distinctSrids,
        surface: args.surface,
      }),
    [args.engine, args.geomId, args.isDeclared, args.dataType, distinctSrids, args.surface],
  );

  return { distinctSrids, probeContext };
}
